import { Area, AreaChart, CartesianGrid, ReferenceDot, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis, type LabelProps } from 'recharts';
import type { SalesPerformance } from '@/types/sales-performance.types';
import { money } from './format';

function RevenueBadge({ viewBox, value }: LabelProps) {
  if (!viewBox || !('x' in viewBox) || !('y' in viewBox)) return null;
  const x = viewBox.x ?? 0;
  const y = viewBox.y ?? 0;
  const label = String(value ?? '');
  const width = Math.max(50, label.length * 6 + 18);
  return <g transform={`translate(${x - width / 2}, ${y - 22})`}>
    <rect width={width} height={22} rx={11} fill='var(--color-indigo-500)' />
    <text x={width / 2} y={14} textAnchor='middle' fill='var(--color-white)' fontSize={10} fontWeight={500}>{label}</text>
  </g>;
}

export function PerformanceRevenue({ report }: { report: SalesPerformance }) {
  const points = report.revenueOverTime.map((point) => ({ ...point, revenue: Number(point.revenue) }));
  const last = points.at(-1);
  const maximum = Math.max(1, ...points.map((point) => point.revenue));
  const magnitude = 10 ** Math.floor(Math.log10(maximum / 4));
  const step = Math.ceil(maximum / 4 / magnitude) * magnitude;
  const ceiling = Math.ceil(maximum / step) * step;
  const dateLabel = (date: string) => new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${date}T00:00:00Z`));
  const currencyLabel = (value: number) => report.currency
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency: report.currency, currencyDisplay: 'narrowSymbol', notation: 'compact', maximumFractionDigits: 0 }).format(value)
    : String(value);

  return <section aria-label='Revenue over time'>
    <h2 className='mb-4 text-xs font-normal leading-4 text-gray-800'>Revenue over time</h2>
    <div className='h-[218px] rounded-3xl border border-gray-500 px-5 pt-8 pb-3 [&_.recharts-surface]:overflow-visible'>
      {points.length ? <ResponsiveContainer width='100%' height='100%'>
        <AreaChart data={points} margin={{ right: 12, top: 14, bottom: 0, left: 0 }}>
          <CartesianGrid stroke='var(--color-gray-300)' strokeDasharray='3 3' />
          <XAxis dataKey='date' tickFormatter={dateLabel} tick={{ fill: 'var(--color-gray-800)', fontSize: 10 }} axisLine={false} tickLine={false} minTickGap={70} height={25} tickMargin={10} />
          <YAxis domain={[0, ceiling]} tickCount={5} tickFormatter={currencyLabel} tick={{ fill: 'var(--color-gray-800)', fontSize: 10 }} axisLine={false} tickLine={false} width={40} />
          <Tooltip labelFormatter={(label) => dateLabel(String(label))} formatter={(value: number) => [money(value, report.currency), 'Revenue']} contentStyle={{ background: 'var(--color-white)', borderColor: 'var(--color-gray-400)', borderRadius: 8, color: 'var(--color-ink)' }} />
          <Area type='linear' dataKey='revenue' stroke='var(--color-indigo-500)' fill='var(--color-indigo-500)' fillOpacity={.12} strokeWidth={1.5} dot={{ r: 1.5, fill: 'var(--color-white)', strokeWidth: 1 }} activeDot={{ r: 3 }} />
          {last && <ReferenceLine segment={[{ x: last.date, y: last.revenue }, { x: last.date, y: ceiling }]} stroke='var(--color-indigo-400)' strokeWidth={1} label={<RevenueBadge value={money(last.revenue, report.currency)} />} />}
          {last && <ReferenceDot x={last.date} y={last.revenue} r={2.5} fill='var(--color-white)' stroke='var(--color-indigo-500)' strokeWidth={1.5} />}
        </AreaChart>
      </ResponsiveContainer> : <p className='flex h-full items-center justify-center text-sm text-gray-800'>No attributed revenue in this period.</p>}
    </div>
  </section>;
}
